import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSyncWatch } from "../context/SyncContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Move, History, Package, Search, Wrench, AlertTriangle, MapPin, Store as StoreIcon, Droplet, ShoppingCart, LayoutGrid, Grid3x3, Download, FileText, Camera, Share2, Send, X, Upload, QrCode, DatabaseBackup, RotateCcw, ArrowLeftRight, HardHat, BarChart3, ArrowRight } from "lucide-react";
import { exportCSV } from "../lib/csvExport";
import { exportBrandedPDF } from "../lib/pdfExport";
import { Checkbox } from "../components/ui/checkbox";
import ActivityFeed from "../components/ActivityFeed";
import { ToolColorHistory } from "../components/ToolColorHistory";

// ============== Bulk Select Context — now in ./inventar/BulkSelectContext ==============

import { useConfirm } from "../components/ConfirmProvider";
import { BulkSelectContext, useBulkSelect, BulkCheckbox, BulkHeaderCheck } from "./inventar/BulkSelectContext";
import OrdersDialog from "./inventar/OrdersDialog";
import ColorSetDialog from "./inventar/ColorSetDialog";
import LocationsManager from "./inventar/LocationsManager";
import ItemDialog from "./inventar/ItemDialog";
import { ITEM_TYPES, EMPTY_ITEM, TYPE_LABELS, TYPE_ICONS, TYPE_COLORS } from "./inventar/constants";
import HistoryDialog from "./inventar/HistoryDialog";
import IssueDialog from "./inventar/IssueDialog";
import RepairEquipmentDialog from "./inventar/RepairEquipmentDialog";
import PSAHistoryDialog from "./inventar/PSAHistoryDialog";
import TransfersHistoryDialog from "./inventar/TransfersHistoryDialog";
import StatsView from "./inventar/StatsView";
import MyStockView from "./inventar/MyStockView";
import TypeSummaryCards from "./inventar/TypeSummaryCards";
import LocationsView from "./inventar/LocationsView";
import ProductsView from "./inventar/ProductsView";
import MagazieStockView from "./inventar/MagazieStockView";
import PSAView from "./inventar/PSAView";
import CarPartsView from "./inventar/CarPartsView";

export default function Inventar() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [meta, setMeta] = useState({ locations: [], colors: [], shelf_locations: [], rows: [], max_col: 30 });
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [cars, setCars] = useState([]);
  const [oilStock, setOilStock] = useState({ total_liters: 0, containers: [] });
  const [persons, setPersons] = useState([]);
  const [historyCounts, setHistoryCounts] = useState({});
  const [filters, setFilters] = useState({ q: "", type: "all", location: "all", color: "all", status: "all" });
  const [activeTab, setActiveTab] = useState("locations");
  const [ordersDialogOpen, setOrdersDialogOpen] = useState(false);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  // Dialog state pentru adăugare/editare piesă auto din tab-ul Piese auto
  const [partsDialog, setPartsDialog] = useState(null); // null | { mode: "add"|"edit", item?, prefill? }
  // ===== Bulk Delete Mode (admin only) =====
  const [bulkMode, setBulkMode] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkAllConfirmOpen, setBulkAllConfirmOpen] = useState(false);
  const [bulkAllText, setBulkAllText] = useState("");
  const bulkCtx = useMemo(() => ({
    bulkMode,
    selected: bulkSelected,
    toggle: (id) => {
      setBulkSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    },
  }), [bulkMode, bulkSelected]);
  // Reset selection when exiting bulk mode
  useEffect(() => { if (!bulkMode) setBulkSelected(new Set()); }, [bulkMode]);
  // Sync filter from URL ?status=defect (used by Magazioner sidebar shortcut)
  const [searchParams] = useSearchParams();
  const [initialCarFilter, setInitialCarFilter] = useState(null);
  useEffect(() => {
    const urlStatus = searchParams.get("status");
    if (urlStatus && ["defect", "functional", "low", "all"].includes(urlStatus)) {
      setFilters((f) => ({ ...f, status: urlStatus }));
    }
    const urlTab = searchParams.get("tab");
    if (urlTab && ["locations", "products", "psa", "car-parts", "stores", "my-stock", "stats"].includes(urlTab)) {
      setActiveTab(urlTab);
    }
    const urlCar = searchParams.get("car");
    if (urlCar) setInitialCarFilter(urlCar);
  }, [searchParams]);

  const load = async () => {
    const [m, i, s, c, o, ord, ps, hc] = await Promise.all([
      api.get("/inventory/meta"),
      api.get("/inventory"),
      api.get("/inventory-stores"),
      api.get("/cars").catch(() => ({ data: [] })),
      api.get("/inventory/oil-stock").catch(() => ({ data: { total_liters: 0, containers: [] } })),
      api.get("/inventory/orders?status=pending").catch(() => ({ data: [] })),
      api.get("/persons").catch(() => ({ data: [] })),
      api.get("/inventory/history-counts").catch(() => ({ data: {} })),
    ]);
    setMeta(m.data);
    setItems(i.data || []);
    setStores(s.data || []);
    setCars(c.data || []);
    setOilStock(o.data || { total_liters: 0, containers: [] });
    setPendingOrdersCount((ord.data || []).length);
    setPersons((ps.data || []).filter((p) => p.active !== false));
    setHistoryCounts(hc.data || {});
  };
  useEffect(() => { load(); }, []);
  useSyncWatch(["inventory", "inventory_history"], () => { load(); });

  const filteredItems = useMemo(() => {
    const q = (filters.q || "").toLowerCase().trim();
    return items.filter((it) => {
      if (q && !`${it.name || ""} ${it.note || ""}`.toLowerCase().includes(q)) return false;
      if (filters.type !== "all" && it.item_type !== filters.type) return false;
      if (filters.location !== "all" && it.location !== filters.location) return false;
      if (filters.color !== "all" && it.color !== filters.color) return false;
      if (filters.status === "defect" && it.status !== "defect") return false;
      if (filters.status === "functional" && it.status === "defect") return false;
      if (filters.status === "low" && (it.quantity || 0) > 1) return false;
      return true;
    });
  }, [items, filters]);
  const hasActiveFilter = filters.q || filters.type !== "all" || filters.location !== "all" || filters.color !== "all" || filters.status !== "all";

  return (
    <div data-testid="page-inventar">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Inventar</h1>
          <p className="text-sm text-zinc-500 mt-1">Echipamente, consumabile, piese auto · locații + culori echipe + rafturi</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setOrdersDialogOpen(true)}
            data-testid="goto-inventory-orders"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 relative"
          >
            🛒 Comenzi
            {pendingOrdersCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold" data-testid="pending-orders-badge">
                {pendingOrdersCount}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/inventar/imprumuturi")}
            data-testid="goto-loaned-equipment"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            📦 Echipamente împrumutate
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/inventar/santier")}
            data-testid="goto-site-leftovers"
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
          >
            🚧 Echipamente șantier
          </Button>
          <Button
            variant="outline"
            data-testid="export-inventory-csv"
            onClick={() => exportCSV(items, [
              { key: "name", label: "Denumire" },
              { key: "item_type", label: "Tip" },
              { key: "location", label: "Locație" },
              { key: (r) => (r.shelf_row && r.shelf_col ? `${r.shelf_row}${r.shelf_col}` : ""), label: "Raft" },
              { key: "color", label: "Culoare echipă" },
              { key: "quantity", label: "Cantitate" },
              { key: "unit", label: "UM" },
              { key: (r) => (r.status === "defect" ? "Defect" : "Funcțional"), label: "Status" },
              { key: (r) => (r.is_oil ? "Da" : ""), label: "Ulei" },
              { key: "warranty_until", label: "Garanție până la" },
              { key: "purchase_date", label: "Data achiziție" },
            ], "inventar")}
          >
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            data-testid="export-inventory-pdf"
            onClick={async () => {
              try {
                await exportBrandedPDF({
                  title: "Raport inventar",
                  subtitle: `${filteredItems.length} ${filteredItems.length === 1 ? "produs" : "produse"}${filteredItems.length !== items.length ? ` (filtrat din ${items.length})` : ""} · stoc ulei: ${(oilStock.total_liters || 0).toFixed(2)} L`,
                  sections: [{
                    table: {
                      columns: ["Denumire", "Tip", "Locație", "Raft", "Cant.", "Status"],
                      rows: filteredItems.map((it) => [
                        it.name || "—",
                        ITEM_TYPES.find((t) => t.code === it.item_type)?.label || it.item_type || "—",
                        it.location || "—",
                        (it.shelf_row && it.shelf_col) ? `${it.shelf_row}${it.shelf_col}` : "—",
                        it.quantity != null ? `${it.quantity}${it.unit ? " " + it.unit : ""}` : "—",
                        it.status === "defect" ? "DEFECT" : "Funcțional",
                      ]),
                    },
                  }],
                  filename: `inventar-${new Date().toISOString().slice(0, 10)}.pdf`,
                });
                toast.success("PDF generat");
              } catch { toast.error("Eroare PDF"); }
            }}
          >
            <FileText size={16} className="mr-2"/> Export PDF
          </Button>
          <div className="card-flat p-4 flex items-center gap-3 min-w-[220px]" data-testid="oil-stock-widget">
          <div className="w-10 h-10 rounded-md bg-[#FEF3C7] grid place-items-center text-[#D97706]">
            <Droplet size={20}/>
          </div>
          <div>
            <div className="small-label">Stoc ulei (real-time)</div>
            <div className="font-display text-2xl font-extrabold leading-none mt-1" data-testid="oil-stock-total">
              {(oilStock.total_liters || 0).toFixed(2)} <span className="text-sm text-zinc-500 font-normal">L</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {oilStock.containers?.length || 0} {oilStock.containers?.length === 1 ? "recipient" : "recipiente"}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Quick-filter buttons: locații (4) + culori (6) + total */}
      <div className="card-flat p-3 mb-3 flex items-center gap-1.5 flex-wrap" data-testid="inv-quickfilters">
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className="text-xs font-bold px-3 py-1.5 rounded-md border-2 border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center gap-1.5"
          data-testid="inv-history-btn"
          title="Toate mișcările: echipamente, PSA, reparații, scule"
        >
          🕘 Istoric
        </button>
        {historyOpen && (
          <Dialog open={true} onOpenChange={(v) => !v && setHistoryOpen(false)}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="inv-history-dialog">
              <DialogHeader>
                <DialogTitle>🕘 Istoric inventar — toate mișcările</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-zinc-500 -mt-2">
                Echipamente adăugate/modificate/șterse, predări-preluări PSA, transferuri, reparații și seturi de scule — cine, ce și când.
              </div>
              <ActivityFeed category="inventar-full" pageSize={30} />
            </DialogContent>
          </Dialog>
        )}
        <span className="text-zinc-300 px-1 select-none">·</span>
        {isAdmin && (
          <>
            <button
              type="button"
              onClick={() => setBulkMode((v) => !v)}
              className={`text-xs font-bold px-3 py-1.5 rounded-md border-2 transition-colors flex items-center gap-1.5 ${bulkMode ? "bg-red-600 text-white border-red-600 hover:bg-red-700" : "bg-white border-red-300 text-red-700 hover:bg-red-50"}`}
              data-testid="inv-bulk-mode-toggle"
              title="Activează modul de selecție multiplă pentru ștergere"
            >
              <Trash2 size={12}/>
              {bulkMode ? "✕ Ieși din modul selecție" : "🗑 Mod selecție"}
            </button>
            <span className="text-zinc-300 px-1 select-none">·</span>
          </>
        )}        <button
          type="button"
          onClick={() => {
            setFilters({ q: "", type: "all", location: "all", color: "all", status: "all" });
            setActiveTab("products");
          }}
          className="text-xs font-bold px-3 py-1.5 rounded-md border-2 border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors flex items-center gap-1.5"
          data-testid="inv-quickfilter-total"
          title="Vezi absolut tot stocul, alfabetic, fără filtre"
        >
          <Package size={12}/>
          Inventar total
        </button>
        <span className="text-zinc-300 px-1 select-none">·</span>
        {[
          { label: "Magazie", value: "MAGAZIE" },
          { label: "Garaj Constantin", value: "GARAJ CONSTANTIN" },
          { label: "Acasă Victor", value: "ACASA VICTOR" },
          { label: "PAXLI", value: "PAXLI" },
        ].map((b) => {
          const active = filters.location === b.value;
          return (
            <button
              key={b.value}
              type="button"
              onClick={() => setFilters({ ...filters, location: active ? "all" : b.value })}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
              data-testid={`inv-quickfilter-loc-${b.value.toLowerCase().replace(/\s+/g, "-")}`}
              title={`Filtrează după locația ${b.label}`}
            >
              <MapPin size={12} className="inline mr-1 -mt-0.5"/>
              {b.label}
            </button>
          );
        })}
        <span className="text-zinc-300 px-1 select-none">·</span>
        {[
          { label: "Roșu", value: "rosu", hex: "#EF4444" },
          { label: "Galben", value: "galben", hex: "#FDE047" },
          { label: "Albastru", value: "albastru", hex: "#3B82F6" },
          { label: "Verde", value: "verde", hex: "#10B981" },
          { label: "Gri", value: "gri", hex: "#6B7280" },
          { label: "Mov", value: "mov", hex: "#A855F7" },
        ].map((b) => {
          const active = filters.color === b.value;
          return (
            <button
              key={b.value}
              type="button"
              onClick={() => setFilters({ ...filters, color: active ? "all" : b.value })}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md border-2 transition-colors flex items-center gap-1.5 ${active ? "text-white shadow-md" : "bg-white hover:bg-zinc-50"}`}
              style={{
                borderColor: b.hex,
                background: active ? b.hex : undefined,
                color: active ? "white" : b.hex,
              }}
              data-testid={`inv-quickfilter-color-${b.value}`}
              title={`Filtrează după setul de culoare ${b.label}`}
            >
              <span className="w-2.5 h-2.5 rounded-full border" style={{ background: b.hex, borderColor: active ? "white" : b.hex }}/>
              {b.label}
            </button>
          );
        })}
      </div>

      {/* Istoric set de scule pentru culoarea selectată (șantiere + persoane) */}
      {filters.color !== "all" && <ToolColorHistory color={filters.color} />}

      <div className="card-flat p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="inv-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-zinc-400"/>
          <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Caută denumire/notă…" className="border-0 shadow-none focus-visible:ring-0 h-8" data-testid="inv-filter-search"/>
        </div>
        <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
          <SelectTrigger className="w-[160px] h-8" data-testid="inv-filter-type"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate tipurile</SelectItem>
            {ITEM_TYPES.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.location} onValueChange={(v) => setFilters({ ...filters, location: v })}>
          <SelectTrigger className="w-[180px] h-8" data-testid="inv-filter-location"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate locațiile</SelectItem>
            {meta.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.color} onValueChange={(v) => setFilters({ ...filters, color: v })}>
          <SelectTrigger className="w-[140px] h-8"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate culorile</SelectItem>
            {meta.colors.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-[150px] h-8"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Orice status</SelectItem>
            <SelectItem value="functional">Funcțional</SelectItem>
            <SelectItem value="defect">Defect</SelectItem>
            <SelectItem value="low">Stoc minim (≤1)</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilter && <button onClick={() => setFilters({ q: "", type: "all", location: "all", color: "all", status: "all" })} className="text-xs text-zinc-500 hover:text-[var(--primary)] underline">Reset</button>}
        <span className="text-xs text-zinc-500 ml-auto">{filteredItems.length} / {items.length} produse</span>
      </div>

      {/* Bulk select sticky toolbar (admin only) */}
      {bulkMode && isAdmin && (
        <div className="sticky top-0 z-30 mb-3 card-flat p-3 bg-red-50 border-2 border-red-300 flex items-center gap-2 flex-wrap" data-testid="inv-bulk-toolbar">
          <span className="text-sm font-bold text-red-800 flex items-center gap-1.5">
            <Trash2 size={14}/>
            Modul selecție activ — {bulkSelected.size} produse selectate
          </span>
          <span className="text-zinc-300 px-1">·</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBulkSelected(new Set((filteredItems || []).map((i) => i.id)))}
            data-testid="inv-bulk-select-all"
            disabled={bulkDeleting}
          >
            ☑ Selectează tot ({filteredItems.length})
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBulkSelected(new Set())}
            data-testid="inv-bulk-deselect-all"
            disabled={bulkDeleting || bulkSelected.size === 0}
          >
            ☐ Deselectează tot
          </Button>
          <span className="flex-1"/>
          <Button
            size="sm"
            onClick={async () => {
              const ids = Array.from(bulkSelected);
              if (ids.length === 0) return;
              if (!window.confirm(`Ștergi definitiv ${ids.length} produs${ids.length === 1 ? "" : "e"}? Această acțiune NU poate fi anulată.`)) return;
              setBulkDeleting(true);
              try {
                const { data } = await api.post("/inventory/bulk-delete", { ids });
                toast.success(`${data.deleted || 0} produse șterse definitiv`);
                setBulkSelected(new Set());
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Eroare la ștergere");
              } finally {
                setBulkDeleting(false);
              }
            }}
            disabled={bulkDeleting || bulkSelected.size === 0}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="inv-bulk-delete-selected"
          >
            <Trash2 size={12} className="mr-1"/>
            {bulkDeleting ? "Se șterge..." : `Șterge selecția (${bulkSelected.size})`}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setBulkAllText(""); setBulkAllConfirmOpen(true); }}
            disabled={bulkDeleting}
            className="border-red-700 text-red-700 hover:bg-red-100"
            data-testid="inv-bulk-delete-all-open"
          >
            ⚠️ Șterge TOATE
          </Button>
        </div>
      )}

      <BulkSelectContext.Provider value={bulkCtx}>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="locations" data-testid="tab-locations"><MapPin size={14} className="mr-1.5"/> Locații / Culori</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products"><Package size={14} className="mr-1.5"/> Produse</TabsTrigger>
          <TabsTrigger value="psa" data-testid="tab-psa"><HardHat size={14} className="mr-1.5"/> PSA</TabsTrigger>
          <TabsTrigger value="car-parts" data-testid="tab-car-parts"><Wrench size={14} className="mr-1.5"/> Piese auto</TabsTrigger>
          <TabsTrigger value="stores" data-testid="tab-stores"><StoreIcon size={14} className="mr-1.5"/> Magazie</TabsTrigger>
          <TabsTrigger value="my-stock" data-testid="tab-my-stock"><Package size={14} className="mr-1.5"/> Stocul meu</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats"><BarChart3 size={14} className="mr-1.5"/> Statistici</TabsTrigger>
        </TabsList>
        <TabsContent value="locations">
          <LocationsView meta={meta} items={filteredItems} stores={stores} cars={cars} onChanged={load} />
        </TabsContent>
        <TabsContent value="products">
          <ProductsView meta={meta} items={filteredItems} stores={stores} cars={cars} persons={persons} historyCounts={historyCounts} onChanged={load} />
        </TabsContent>
        <TabsContent value="psa">
          <PSAView meta={meta} items={items.filter((i) => i.item_type === "psa")} cars={cars} persons={persons} historyCounts={historyCounts} onChanged={load} />
        </TabsContent>
        <TabsContent value="car-parts">
          <CarPartsView
            items={items}
            cars={cars}
            initialCarFilter={initialCarFilter}
            onAddPart={(carId) => setPartsDialog({ mode: "add", prefill: { item_type: "piesa_auto", linked_car_id: carId || "" } })}
            onEdit={(it) => setPartsDialog({ mode: "edit", item: it })}
            onChanged={load}
          />
        </TabsContent>
        <TabsContent value="stores">
          <MagazieStockView items={items} />
        </TabsContent>
        <TabsContent value="my-stock">
          <MyStockView items={items} />
        </TabsContent>
        <TabsContent value="stats">
          <StatsView meta={meta} items={items} />
        </TabsContent>
      </Tabs>
      </BulkSelectContext.Provider>

      {/* ItemDialog deschis din tab-ul Piese auto (add/edit) */}
      {partsDialog && (
        <ItemDialog
          mode={partsDialog.mode}
          item={partsDialog.item}
          prefill={partsDialog.prefill}
          meta={meta}
          stores={stores}
          cars={cars}
          onClose={() => setPartsDialog(null)}
          onSaved={() => { setPartsDialog(null); load(); }}
        />
      )}

      {/* Confirmare nucleară: Șterge TOATE produsele */}
      {bulkAllConfirmOpen && (
        <Dialog open={true} onOpenChange={(o) => { if (!o && !bulkDeleting) { setBulkAllConfirmOpen(false); setBulkAllText(""); } }}>
          <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()} data-testid="bulk-all-confirm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle size={20}/>
                Șterge TOATE produsele
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="text-sm text-zinc-800 bg-red-50 border-2 border-red-300 rounded p-3">
                <strong>Atenție:</strong> Această acțiune va șterge definitiv <strong>{items.length}</strong> produse din inventar.
                Toate datele despre echipamente, consumabile, PSA și piese auto vor dispărea. Acțiunea NU poate fi anulată.
                <br/><br/>
                Istoricul rămâne în jurnalul de audit (`inventory_history`) cu acțiunea `bulk_delete`.
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                  Pentru a confirma, tastează exact: <code className="bg-zinc-100 px-1 rounded">STERGE TOT</code>
                </Label>
                <Input
                  value={bulkAllText}
                  onChange={(e) => setBulkAllText(e.target.value)}
                  placeholder="STERGE TOT"
                  className="mt-1 font-mono uppercase"
                  data-testid="bulk-all-confirm-input"
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setBulkAllConfirmOpen(false); setBulkAllText(""); }} disabled={bulkDeleting} data-testid="bulk-all-cancel">
                Anulează
              </Button>
              <Button
                onClick={async () => {
                  setBulkDeleting(true);
                  try {
                    const { data } = await api.post("/inventory/bulk-delete", { all: true });
                    toast.success(`${data.deleted || 0} produse șterse — inventar gol`);
                    setBulkAllConfirmOpen(false);
                    setBulkAllText("");
                    setBulkSelected(new Set());
                    setBulkMode(false);
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Eroare la ștergere totală");
                  } finally {
                    setBulkDeleting(false);
                  }
                }}
                disabled={bulkDeleting || bulkAllText.trim().toUpperCase() !== "STERGE TOT"}
                className="bg-red-700 hover:bg-red-800 text-white"
                data-testid="bulk-all-confirm-btn"
              >
                {bulkDeleting ? "Se șterge..." : `⚠️ Da, șterge toate (${items.length})`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {ordersDialogOpen && (
        <OrdersDialog
          open={ordersDialogOpen}
          onClose={() => setOrdersDialogOpen(false)}
          stores={stores}
          items={items}
          onChanged={load}
        />
      )}
      <div className="mt-8 card-flat p-6">
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2">
          📱 Cronologie Inventar
          <span className="text-xs text-zinc-500 font-normal">— cine a predat / preluat / transferat</span>
        </h2>
        <ActivityFeed category="inventory" pageSize={8} compact/>
      </div>
    </div>
  );
}

// ---------- Item type labels and styling — now in ./inventar/constants ----------


// ---------- Locations View ----------
// ---------- Products View ----------
// ---------- Stores View ----------
// ---------- Shelf Grid (vizualizare grafică rafturi) ----------
// ---------- Group items by category (name) ----------
// Prioritizare specială pentru magazioner: definiția PRIORITY_PATTERNS s-a mutat
// în `/app/frontend/src/pages/inventar/helpers.js` unde este și folosită
// (categoryPriorityRank). Aici era orphan și producea ReferenceError în runtime
// pentru că `helpers.js` nu o importa.

// ---------- CategoryCard (one per product type, e.g. "Mașină de Șlefuit") ----------
// ---------- CategoryGroupDialog (lists individual items in a group) ----------

// ---------- ItemCard ----------
// ---------- Photo Gallery + Share Dialog ----------

// ---------- Repair Equipment Dialog ----------


// ---------- Issue Dialog (preluare din magazie) ----------
// ---------- My Stock View (preluări proprii + reparații proprii) ----------

// ---------- PSA View (Personal Protective Equipment) ----------




// ---------- Transfers History Dialog ----------

// ---------- PSA History Dialog ----------



// ---------- Stats View — global donut + per-item-name distribution table ----------

